'use client';

import { styled } from 'styled-components';
import * as Styled from '@lib/styles';
import TextLink from './TextLink';
import Button from './Button';
import type { Color, CtaProps } from '@types';
import type { ReactNode } from 'react';

type BaseTextProps = {
  /**
   * Heading for the base text
   */
  heading?: string;
  /**
   * Heading level
   */
  headingLevel?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  /**
   * Body text for the base text
   * - Use a simple string (e.g. "A sentence of words.") for straight copy.
   * - Use HTML to apply simple formatting (e.g. A sentence with an <strong>important</strong> word.)
   */
  body?: ReactNode;
  /**
   * Optional className for styling
   */
  className?: string;
  /**
   * Optional CTA
   * Note: Not to be used with buttons; Meaning there should not be
   * a primary or secondary button when using this option
   */
  ctaLink?: CtaProps;
  /**
   * Is text centered? Default is false
   */
  isCentered?: boolean;
  /**
   * Primary button
   * Note: Not to be used with the CTA; Meaning there should
   * not be a link displayed when using buttons
   */
  primaryBtn?: CtaProps;
  /**
   * Secondary button
   * Note: Not to be used with the CTA; Meaning there should
   * not be a link displayed when using buttons
   */
  secondaryBtn?: CtaProps;
  /**
   * Eyebrow text
   */
  eyebrow?: string;
  /**
   * Foreground color of the secondary button
   */
  buttonColor?: Color;
};

const BaseTextWrapper = styled.div<{ $isCentered }>`
  text-align: ${({ $isCentered }) => ($isCentered ? 'center' : 'start')};
  text-wrap: ${({ $isCentered }) => ($isCentered ? 'balance' : 'unset')};
`;

// Unstyled heading necessary to use the as prop
const Heading = styled.h2``;

const ButtonRow = styled.div<{ $isCentered }>`
  align-items: ${({ $isCentered }) => ($isCentered ? 'center' : 'start')};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[1.5]};
  justify-content: ${({ $isCentered }) => ($isCentered ? 'center' : 'start')};

  ${({ theme }) => theme.mq.minWidth.medium} {
    flex-direction: row;
  }
`;

// Needs to be a div to style spacing in consumed components/modules
const BaseText = ({
  heading,
  headingLevel = 'h2',
  body,
  className,
  ctaLink = null,
  isCentered = false,
  primaryBtn = null,
  secondaryBtn = null,
  buttonColor,
  eyebrow,
}: BaseTextProps) => {
  const computedBody = () => {
    const descriptionWrappingElement =
      typeof body === 'string' ? { as: 'p' } : { as: 'div' };

    switch (headingLevel) {
      case 'h1':
        return (
          <Styled.BodyLg {...descriptionWrappingElement}>{body}</Styled.BodyLg>
        );
      case 'h4':
        return (
          <Styled.BodySm {...descriptionWrappingElement}>{body}</Styled.BodySm>
        );
      case 'h5':
      case 'h6':
        return (
          <Styled.BodyXs {...descriptionWrappingElement}>{body}</Styled.BodyXs>
        );
      default:
        return (
          <Styled.BodyMd {...descriptionWrappingElement}>{body}</Styled.BodyMd>
        );
    }
  };

  const computedHref = () => {
    if (!ctaLink) return null;

    switch (headingLevel) {
      case 'h1':
        return (
          <Styled.BodyLg>
            <TextLink cta={ctaLink} />
          </Styled.BodyLg>
        );
      case 'h6':
      case 'h5':
        return (
          <Styled.BodyXs>
            <TextLink cta={ctaLink} />
          </Styled.BodyXs>
        );
      case 'h4':
        return (
          <Styled.BodySm>
            <TextLink cta={ctaLink} />
          </Styled.BodySm>
        );
      case 'h3':
        return (
          <Styled.BodyMd>
            <TextLink cta={ctaLink} />
          </Styled.BodyMd>
        );
      default:
        return (
          <p>
            <TextLink cta={ctaLink} />
          </p>
        );
    }
  };

  return (
    <BaseTextWrapper $isCentered={isCentered} className={className}>
      {eyebrow ? <Styled.Eyebrow>{eyebrow}</Styled.Eyebrow> : null}
      {heading ? <Heading as={headingLevel}>{heading}</Heading> : null}
      {body ? computedBody() : null}
      {primaryBtn || secondaryBtn ? (
        <ButtonRow $isCentered={isCentered}>
          {primaryBtn ? (
            // Primary Button in BaseText is always brand blue according to Figma
            <Button
              color="blue500"
              href={primaryBtn.href}
              trackingLabel={primaryBtn.trackingLabel}
              trackingProperty={primaryBtn.trackingProperty}
            >
              {primaryBtn.text}
            </Button>
          ) : null}
          {secondaryBtn ? (
            <Button
              color={buttonColor}
              href={secondaryBtn.href}
              trackingLabel={secondaryBtn.trackingLabel}
              trackingProperty={secondaryBtn.trackingProperty}
              variant="secondary"
            >
              {secondaryBtn.text}
            </Button>
          ) : null}
        </ButtonRow>
      ) : null}
      {ctaLink ? computedHref() : null}
    </BaseTextWrapper>
  );
};

export default BaseText;
